import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { checkPhone } from '../../lib/phone';

/** Name + phone recipient editor (max `max`). Same list shape the SMS layer
 *  already normalizes: [{ name, phone }]. Each number is validated on blur by
 *  the server's own parser, and `onValidityChange(false)` lets the parent block
 *  the save rather than quietly dropping a bad recipient. */
export const RecipientListEditor = ({ value = [], onChange, max = 10, disabled = false, testid = 'recipients', onValidityChange }) => {
    const { t } = useTranslation();
    const [errors, setErrors] = useState({});
    const rows = Array.isArray(value) ? value : [];

    const publish = (next) => {
        setErrors(next);
        if (onValidityChange) onValidityChange(!Object.values(next).some(Boolean));
    };

    const upd = (i, key, val) => {
        if (key === 'phone' && errors[i]) publish({ ...errors, [i]: null });
        onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
    };

    const verify = async (i, raw) => {
        // The SERVER decides what is dialable; we only localize the sentence.
        const { valid } = await checkPhone(raw);
        publish({ ...errors, [i]: valid ? null : t('phone.invalid', { value: String(raw).trim() }) });
    };

    const remove = (i) => {
        const next = {};
        Object.keys(errors).forEach((k) => {
            const n = Number(k);
            if (n < i) next[n] = errors[k];
            else if (n > i) next[n - 1] = errors[k];
        });
        publish(next);
        onChange(rows.filter((_, idx) => idx !== i));
    };

    return (
        <div className="space-y-2" data-testid={testid}>
            {rows.map((r, i) => (
                <div key={i} className="space-y-1">
                    <div className="flex gap-2 items-center">
                        <Input
                            className="flex-1" placeholder={t('broadcast.name', 'Name')}
                            value={r.name || ''} disabled={disabled}
                            onChange={(e) => upd(i, 'name', e.target.value)}
                            data-testid={`${testid}-name-${i}`}
                        />
                        <Input
                            className={`flex-1 ${errors[i] ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                            placeholder={t('broadcast.phone', 'Mobile number')}
                            value={r.phone || ''} disabled={disabled} inputMode="tel"
                            onChange={(e) => upd(i, 'phone', e.target.value)}
                            onBlur={(e) => verify(i, e.target.value)}
                            data-testid={`${testid}-phone-${i}`}
                        />
                        <Button type="button" size="icon" variant="ghost" disabled={disabled}
                            onClick={() => remove(i)}
                            data-testid={`${testid}-remove-${i}`}>
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </div>
                    {errors[i] && (
                        <p className="text-xs text-red-500 pl-1" data-testid={`${testid}-error-${i}`}>{errors[i]}</p>
                    )}
                </div>
            ))}
            {rows.length === 0 && (
                <p className="text-xs text-muted-foreground" data-testid={`${testid}-empty`}>
                    {t('broadcast.noRecipients', 'Nobody is on this list yet.')}
                </p>
            )}
            <Button type="button" variant="outline" size="sm" disabled={disabled || rows.length >= max}
                onClick={() => onChange([...rows, { name: '', phone: '' }])}
                data-testid={`${testid}-add`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                {t('broadcast.addRecipient', 'Add person')} ({rows.length}/{max})
            </Button>
        </div>
    );
};

export default RecipientListEditor;
